import { motion } from "framer-motion";
import { Clock3, Route, ArrowUpRight } from "lucide-react";
import { waLink } from "@/lib/site";
import { T, useLang } from "@/lib/i18n";
import { WeatherChip } from "@/components/WeatherBadge";

const IMAGES = {
  salzburg: "/images/hero-salzburg.webp",
  hallstatt: "/images/hallstatt-village.webp",
  zell: "/images/zell-lake.webp",
};

export default function Destinations() {
  const { lang } = useLang();
  const t = T[lang].destinations;

  return (
    <section id="destinations" data-testid="destinations-section" className="relative py-24 lg:py-36 overflow-hidden">
      <img
        src="/images/lake-blue.webp"
        alt=""
        aria-hidden="true"
        loading="lazy" decoding="async"
        className="absolute inset-0 w-full h-full object-cover object-[center_80%]"
      />
      <div className="absolute inset-0 bg-[#16110C]/40" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#16110C] via-[#16110C]/25 to-[#16110C]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-2xl"
        >
          <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-[#C5A059] mb-4">{t.overline}</p>
          <h2 className="font-serif-display font-semibold tracking-tight text-stone-100 text-3xl sm:text-4xl lg:text-5xl">
            {t.h2a}<em className="not-italic text-[#C5A059]">{t.h2b}</em>{t.h2c}
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
          {t.cards.map((d, i) => (
            <motion.article
              key={d.testid}
              data-testid={`destination-card-${d.testid}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: "easeOut" }}
              whileHover={{ scale: 1.03 }}
              className="group relative rounded-3xl overflow-hidden aspect-[3/4] border border-stone-800 shadow-md hover:shadow-2xl hover:shadow-black/60 hover:border-[#C5A059]/30 transition-all duration-500"
            >
              <img
                src={IMAGES[d.testid]}
                alt={d.name}
                loading="lazy" decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
              />
              <div className="absolute top-4 right-4">
                <WeatherChip place={d.testid} />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/90 via-stone-950/25 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-7 text-white">
                <h3 className="font-serif-display font-semibold text-3xl">{d.name}</h3>
                <p className="mt-1 text-sm text-white/80 italic font-serif-display">{d.tagline}</p>
                <div className="mt-4 space-y-1.5 text-xs text-white/75">
                  <p className="flex items-center gap-2"><Route className="w-3.5 h-3.5 text-[#C5A059]" />{d.drive}</p>
                  <p className="flex items-center gap-2"><Clock3 className="w-3.5 h-3.5 text-[#C5A059]" />{d.duration}</p>
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-white/60">{d.spots}</p>
                <a
                  data-testid={`destination-enquire-${d.testid}`}
                  href={waLink(d.wa)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/30 px-5 py-2.5 text-xs font-semibold tracking-wide hover:bg-[#C5A059] hover:border-[#C5A059] hover:text-[#141312] transition-colors duration-300"
                >
                  {t.cta}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
